import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { useSubroutine } from '@/hooks/useSubroutine';
import { useViewportContext } from '@/hooks/useViewport';
import { BackendNode, BackendNodeId } from '@/types/backendNode';
import { ConfigurationEnum } from '@/types/metadata/metadata';
import { Prompt } from '@/types/prompt';
import { SearchBlocksItem } from '@/types/search';
import { KeyValue, Variable } from '@/types/variable';
import { findSubroutine, getSubroutineByNode } from '@/util/application';
import { isArray, isConfigurationEnum, isKeyValue, isNumber, isObject, isVariable } from '@/util/typeGuards';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import {
  Box,
  Card,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, MouseEvent, useEffect, useState } from 'react';

type BlockSearchType = {
  blockId: string;
  block: BackendNode;
  currentSubroutineLabel: string;
  searchValue: string[];
}[];

type FindReferencesDialogProps = {
  searchItem: SearchBlocksItem | undefined;
  onClose: () => void;
};

const defaultMaxResults = 50;

const FindReferencesDialog: FC<FindReferencesDialogProps> = (props) => {
  const theme = useTheme();
  const { application } = useApplication();
  const { metadata } = useMetadata();
  const { selectedSubroutineId } = useSubroutine();
  const { setCenterOnNode } = useViewportContext();

  const [count, setCount] = useState(0);
  const [selectedBlock, setSelectedBlock] = useState<string | undefined>();
  const [listedBlocks, setListedBlocks] = useState<BlockSearchType>();
  const [maxResults, setMaxResults] = useState(defaultMaxResults);
  const [currentSubOnly, setCurrentSubOnly] = useState<boolean>(false);

  const currentSubroutine = findSubroutine(application.subroutines, selectedSubroutineId);

  const handleClose = () => {
    setCurrentSubOnly(false);
    setSelectedBlock(undefined);
    props.onClose();
  };

  const handleSearch = (e: MouseEvent<HTMLButtonElement | HTMLDivElement, globalThis.MouseEvent>, blockId?: string) => {
    setCenterOnNode(blockId || selectedBlock);
    handleClose();
  };

  const getTitle = () => {
    if (props.searchItem?.searchType === 'Subroutine') return 'Find References - Subroutine';
    if (props.searchItem?.searchType === 'Variable')
      return `Find ${props.searchItem?.searchType} - ${props.searchItem?.searchQuery}`;

    if (props.searchItem?.searchType === 'Prompt') {
      const promptLabel = application.prompts.find(({ id }) => props.searchItem?.searchQuery === id.toString())?.label;
      `Find ${props.searchItem?.searchType} - ${promptLabel}`;
    }

    return '';
  };

  const findVariable = (detail: unknown, blockId: string): string => {
    let result = '';
    if (isVariable(detail)) {
      if (
        detail.name?.toLocaleLowerCase() === props.searchItem?.searchQuery.toLocaleLowerCase() &&
        detail.scope === props.searchItem.scope
      ) {
        if (
          props.searchItem?.scope !== 'local' ||
          getSubroutineByNode(application, blockId)?.id === selectedSubroutineId
        )
          result = detail.name;
      }
    } else if (isObject(detail)) {
      Object.entries(detail).forEach(([_, subDetailValue]) => {
        const foundVariable = findVariable(subDetailValue, blockId);
        if (foundVariable) {
          result = foundVariable;
        }
      });
    } else if (isArray(detail)) {
      detail.map((subDetail) => {
        const foundVariable = findVariable(subDetail, blockId);
        if (foundVariable) {
          result = foundVariable;
        }
      });
    }

    return result;
  };

  const searchForVariables = (blockId: string): string[] => {
    const result: string[] = [];

    Object.entries(application.blocks.details[blockId]).forEach(([_, detail]) => {
      const foundVariable = findVariable(detail, blockId);
      foundVariable && result.push(`Variable Name: ${foundVariable}`);
    });

    if (currentSubroutine?.startblock?.toString() === blockId) {
      const bindings = [...currentSubroutine.arg_bindings, ...(currentSubroutine.event_arg_bindings ?? [])];
      bindings.forEach((binding) => {
        if (
          binding.toLocaleLowerCase() === props.searchItem?.searchQuery.toLocaleLowerCase() &&
          props.searchItem.scope === 'local'
        ) {
          result.push(`Variable Name: ${binding}`);
        }
      });
    }

    if (application.blocks.event_handlers && application.blocks.event_handlers[blockId]) {
      Object.entries(application.blocks.event_handlers[blockId]).forEach(([_, handler]) => {
        handler.arguments?.forEach((argument) => {
          if (
            isVariable(argument) &&
            argument.name.toLocaleLowerCase() === props.searchItem?.searchQuery.toLocaleLowerCase()
          ) {
            result.push(`Variable Name: ${argument.name}`);
          }
        });
      });
    }

    return result;
  };

  const findPrompt = (config: ConfigurationEnum, blockId: string, promptToFind: Prompt | undefined): string[] => {
    let result: string[] = [];
    config.sections?.find((section) => {
      section.forEach((innerSection) => {
        const sectionDetailValue = application.blocks.details[blockId][innerSection.value];
        if (innerSection.type === 'prompts' && isArray(sectionDetailValue)) {
          sectionDetailValue.forEach((value: Variable | string) => {
            if (isNumber(value)) {
              const currentPrompt = application.prompts.find((prompt) => prompt.id === value);

              if (currentPrompt?.id === promptToFind?.id) {
                result.push(`Prompt: ${currentPrompt?.file ?? ''}`);
              }
            }
          });
        }
        if (innerSection.type === 'kvvaluelist' && isArray(sectionDetailValue)) {
          sectionDetailValue.forEach((sectionValue: KeyValue<string | number> | string) => {
            if (isKeyValue(sectionValue) && isArray(sectionValue.value)) {
              sectionValue.value.forEach((promptValue) => {
                const currentPrompt = application.prompts.find((prompt) => prompt.id === promptValue);

                if (currentPrompt?.id === promptToFind?.id) {
                  result.push(`Prompt: ${currentPrompt?.file ?? ''}`);
                }
              });
            }
          });
        }
        if (innerSection.sections) {
          result = findPrompt(innerSection, blockId, promptToFind);
        }
      });
    });

    return result;
  };

  const searchForPrompts = (blockId: string, promptToFind: Prompt | undefined): string[] => {
    const result: string[] = [];
    metadata.blocks
      .find((block) => block.type === application.blocks.nodes[blockId].type)
      ?.configuration.forEach((config) => {
        const configDetailValue = application.blocks.details[blockId][config.value];
        if (config.type === 'prompts' && isArray(configDetailValue)) {
          configDetailValue.forEach((value: Variable | string) => {
            if (isNumber(value)) {
              const currentPrompt = application.prompts.find((prompt) => prompt.id === value);

              if (currentPrompt?.id === promptToFind?.id) {
                result.push(`Prompt: ${currentPrompt?.file ?? ''}`);
              }
            }
          });
        }

        if (isConfigurationEnum(config) && config.sections) {
          const foundSection = findPrompt(config, blockId, promptToFind);
          foundSection && foundSection.forEach((prompt) => !result.includes(prompt) && result.push(prompt));
        }
      });

    return result;
  };

  const searchForSubroutines = (blockId: string, block: BackendNode): string[] => {
    const result = [];

    if (application.blocks.event_handlers && application.blocks.event_handlers[blockId]) {
      Object.entries(application.blocks.event_handlers[blockId]).find(([_, eventHandler]) => {
        const subLabel = application.subroutines.find((sub) => sub.id === eventHandler.eventstart)?.label;
        if (subLabel?.toLocaleLowerCase() === props.searchItem?.searchQuery.toLocaleLowerCase()) {
          result.push(`Subroutine: ${subLabel}`);
        }
      });
    }

    if (block.type === 'Gosub' || block.type === 'RunTask') {
      const substartId = application.blocks.details[blockId]['substart'];
      const linkedSubroutine = substartId
        ? application.subroutines.find((subroutine) => subroutine.id === substartId)
        : undefined;
      if (linkedSubroutine) {
        if (linkedSubroutine.label?.toLocaleLowerCase() === props.searchItem?.searchQuery.toLocaleLowerCase()) {
          result.push(`Subroutine: ${linkedSubroutine.label}`);
        }
      }
    }

    return result;
  };

  const currentSubNodes = () => {
    const currentSubroutine = findSubroutine(application.subroutines, selectedSubroutineId);
    const result: Record<BackendNodeId, BackendNode> = {};

    currentSubroutine?.nodes.forEach((node) => (result[node] = application.blocks.nodes[node]));

    return result;
  };

  useEffect(() => {
    if (!props.searchItem) return;

    setCount(0);

    const searchNodes =
      currentSubOnly && props.searchItem?.scope !== 'local' ? currentSubNodes() : application.blocks.nodes;
    const result: BlockSearchType = [];

    switch (props.searchItem.searchType) {
      case 'Variable':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const searchVariables = searchForVariables(blockId);
          if (searchVariables.length) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: searchVariables });
          }
        });
        break;

      case 'Prompt':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const promptToFind = application.prompts.find(({ id }) => props.searchItem?.searchQuery === id.toString());
          const searchPrompts = searchForPrompts(blockId, promptToFind);
          if (searchPrompts.length) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: searchPrompts });
          }
        });
        break;

      case 'Subroutine':
        Object.entries(searchNodes).forEach(([blockId, block]) => {
          const searchBlocks = searchForSubroutines(blockId, block);
          if (searchBlocks.length) {
            const currentSubroutineLabel = getSubroutineByNode(application, blockId)?.label || '';
            setCount((prevCount) => prevCount + 1);
            result.length < maxResults &&
              result.push({ blockId, block, currentSubroutineLabel, searchValue: searchBlocks });
          }
        });
        break;

      default:
    }

    setListedBlocks(result);
  }, [props.searchItem, maxResults, currentSubOnly]);

  return (
    <Dialog open={Boolean(props.searchItem)} onClose={handleClose} fullWidth disableRestoreFocus>
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent
        sx={{
          minHeight: theme.spacing(40),
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Card
          variant='outlined'
          sx={{
            bgcolor: 'transparent',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <List
            sx={{
              maxHeight: theme.spacing(50),
              overflowY: 'auto',
              width: '100%'
            }}
          >
            {listedBlocks?.map((listedBlock) => {
              if (!listedBlock) return;
              const isSelected = listedBlock.blockId === selectedBlock;
              return (
                <ListItemButton
                  key={listedBlock.blockId}
                  selected={isSelected}
                  onClick={() => setSelectedBlock(listedBlock.blockId)}
                  onDoubleClick={(e) => handleSearch(e, listedBlock.blockId)}
                  sx={{ lineBreak: 'anywhere' }}
                >
                  <ListItemIcon sx={{ minWidth: '40px', alignItems: 'center' }}>
                    {isSelected ? (
                      <CheckCircleIcon
                        sx={{
                          color: 'lightgray'
                        }}
                      />
                    ) : (
                      <CircleIcon
                        sx={{
                          color: '#ffffff'
                        }}
                      />
                    )}
                  </ListItemIcon>
                  <Box width='50%' display='flex' flexDirection='row' alignItems='center'>
                    <ListItemText
                      primary={
                        listedBlock.block.label
                          ? `${listedBlock.blockId} - ${listedBlock.block.label}`
                          : listedBlock.blockId
                      }
                      secondary={`${listedBlock.block.type} - ${listedBlock.currentSubroutineLabel}`}
                    />
                  </Box>
                  <ListItemText
                    primary={listedBlock.searchValue.map((searchValue, index) => (
                      <Box key={`searchValue ${index}`} my={'5px'}>
                        {searchValue}
                      </Box>
                    ))}
                    sx={{ display: 'flex', flexDirection: 'column', width: '40%' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Card>
        {count > 0 && (
          <ListItemText
            primary={`Total hits: ${count}`}
            secondary={maxResults === 99999 || count < maxResults ? undefined : 'Showing 50 first hits.'}
            sx={{ mt: '10px', mb: 0, ml: '5px', flex: 0 }}
          />
        )}
        {props.searchItem?.scope !== 'local' && (
          <Tooltip title={'Search only in current subroutine'} placement='top'>
            <Box width={200} display='flex' flexDirection='row' alignItems='center'>
              <Checkbox
                checked={currentSubOnly}
                onChange={(e, checked) => setCurrentSubOnly(checked)}
                color='default'
                sx={{ p: '5px' }}
              />
              <Box
                fontSize={10.275}
                color='#ffffffb3'
                letterSpacing={0.12}
                fontFamily='"Roboto", "Helvetica", "Arial", sans-serif'
              >
                Search only in current subroutine
              </Box>
            </Box>
          </Tooltip>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {count > maxResults && (
            <Button variant='outlined' onClick={() => setMaxResults(99999)} color='secondary'>
              Show All
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={handleClose} color='secondary'>
            Close
          </Button>
          <Button onClick={handleSearch} color='primary' variant='contained' disabled={!selectedBlock}>
            Go to
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FindReferencesDialog;
